import { createForm } from "@tanstack/solid-form";
import type { Component } from "solid-js";
import { createEffect, Show } from "solid-js";
import { produce } from "solid-js/store";
import { toast } from "solid-sonner";
import { z } from "zod";
import { useI18n } from "@/api/i18n";
import { useLayoutContext } from "@/components/layout/context/LayoutContext";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  FieldContainer,
  FieldContainerLabel,
  FieldInfo,
} from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Switch,
  SwitchControl,
  SwitchLabel,
  SwitchThumb,
} from "@/components/ui/switch";
import { useAuthV2 } from "@/domains/auth/context/auth-provider";
import {
  AVAILABILITY_EVENTS,
  availabilitySaveProps,
} from "@/domains/freelance/availability/constants/tracking";
import { isAvailableNow } from "@/domains/freelance/availability/utils/is-available-now";
import { useUpdateProfileAvailability } from "@/domains/freelance/profile/api";
import { EFFORT } from "@/domains/job-offer/create/constants";
import { usePostHog } from "@/hooks/usePostHog";
import { tryCatch } from "@/libs/try-catch";

const availabilitySchema = z.object({
  is_available: z.boolean().default(true),
  weekly_hours: z.enum(EFFORT),
});

type AvailabilityValues = z.infer<typeof availabilitySchema>;

export const AvailabilityModal: Component = () => {
  const [authStoreV2, setAuthStoreV2] = useAuthV2();
  const {
    isAvailabilityModalOpen,
    setIsAvailabilityModalOpen,
    availabilityModalSource,
  } = useLayoutContext();
  const { t } = useI18n();
  const { capture } = usePostHog();

  const updateProfileAvailabilityMutation = useUpdateProfileAvailability();

  const updateProfileAvailability = async (values: AvailabilityValues) => {
    const [_response, error] = await tryCatch(
      updateProfileAvailabilityMutation.mutateAsync({
        weekly_hours: values.weekly_hours,
        available_from: values.is_available ? new Date() : null,
      }),
    );
    if (error) {
      toast.error(t("availability.error") as string);
      return;
    }
    setAuthStoreV2(
      produce((state) => {
        state.profile!.weekly_hours = values.weekly_hours;
        state.profile!.available_from = values.is_available ? new Date() : null;
      }),
    );
    /* Read before the close below: `onOpenChange` runs on every dismissal, and a
       source cleared there would leave this payload guessing. */
    capture(
      AVAILABILITY_EVENTS.save,
      availabilitySaveProps(availabilityModalSource()),
    );
    toast.success(t("availability.success") as string);
    setIsAvailabilityModalOpen(false);
  };

  const form = createForm(() => ({
    defaultValues: {
      is_available: isAvailableNow(authStoreV2?.profile?.available_from),
      weekly_hours: (authStoreV2?.profile?.weekly_hours ??
        "40") as (typeof EFFORT)[number],
    },
    onSubmit: ({ value }) => {
      updateProfileAvailability(value);
    },
  }));

  /**
   * Il modale non si smonta mai — il layout lo tiene montato e `Dialog` ne
   * controlla solo l'apertura — quindi il form sopravvive alla chiusura con le
   * modifiche abbandonate ancora dentro. Con un "Annulla" a schermo la cosa
   * smette di essere un dettaglio: il bottone promette di buttare via le
   * modifiche, e senza questo reset la riapertura le rimetterebbe lì.
   *
   * All'apertura e non alla chiusura, altrimenti i valori tornerebbero indietro
   * a vista durante i 200ms di uscita del dialog.
   */
  createEffect(() => {
    if (isAvailabilityModalOpen()) form.reset();
  });

  return (
    <Dialog
      open={isAvailabilityModalOpen()}
      onOpenChange={setIsAvailabilityModalOpen}
      modal
    >
      <DialogContent class="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("availability.title")}</DialogTitle>
          <DialogDescription>{t("availability.subtitle")}</DialogDescription>
        </DialogHeader>

        <div class="flex flex-col">
          {/* Availability toggle */}
          <form.Field
            name="is_available"
            validators={{ onChange: z.boolean() }}
          >
            {(field) => (
              <Switch
                class="flex items-center gap-3"
                checked={field().state.value}
                onChange={field().setValue}
              >
                <SwitchControl>
                  <SwitchThumb />
                </SwitchControl>
                <SwitchLabel class="cursor-pointer text-sm">
                  <Show
                    when={field().state.value}
                    fallback={t("availability.unavailable")}
                  >
                    {t("availability.available")}
                  </Show>
                </SwitchLabel>
              </Switch>
            )}
          </form.Field>

          {/* Monthly hours select */}
          <form.Subscribe
            selector={(state) => ({ is_available: state.values.is_available })}
          >
            {(state) => (
              /*
                Le ore hanno senso solo per chi è disponibile: da spento il
                campo non è "disabilitato", non c'è proprio. Eccezione
                registrata al non negoziabile «anima solo transform/opacity» —
                l'altezza dipende dal contenuto e `grid-template-rows` +
                `overflow-hidden` è la sola tecnica che la interpola senza
                misurarla in JS (stessa scelta di `job-row.tsx`). Il dialog è
                centrato con una translate del 50%, quindi si riequilibra da
                solo mentre cresce.

                `inert` e non solo il clip: ritagliato, il select resterebbe
                raggiungibile con Tab e aprirebbe un menu dal nulla.
              */
              <div
                class="grid transition-[grid-template-rows] duration-200 ease-out-strong motion-reduce:transition-none"
                style={{
                  "grid-template-rows": state().is_available ? "1fr" : "0fr",
                }}
                inert={!state().is_available}
              >
                <div class="overflow-hidden">
                  {/* La spaziatura sta dentro alla parte che collassa, se no
                      resterebbe un vuoto di 16px sotto allo switch. */}
                  <div class="pt-4">
                    <form.Field
                      name="weekly_hours"
                      validators={{ onChange: z.enum(EFFORT) }}
                    >
                      {(field) => (
                        <FieldContainer>
                          <FieldContainerLabel for={field().name}>
                            {t("availability.weekly_hours")}
                          </FieldContainerLabel>
                          <Select
                            class="bg-white"
                            value={field().state.value}
                            onChange={(value) =>
                              field().handleChange(value ?? field().state.value)
                            }
                            options={[...EFFORT]}
                            itemComponent={(item) => (
                              <SelectItem {...item}>
                                {item.item.rawValue}{" "}
                                {t("availability.h_weekly_hours")}
                              </SelectItem>
                            )}
                          >
                            <SelectTrigger>
                              <SelectValue>
                                <Show
                                  when={field().state.value}
                                  fallback={t("availability.select_hours")}
                                >
                                  {field().state.value}{" "}
                                  {t("availability.h_weekly_hours")}
                                </Show>
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent />
                          </Select>
                          <FieldInfo field={field()} />
                        </FieldContainer>
                      )}
                    </form.Field>
                  </div>
                </div>
              </div>
            )}
          </form.Subscribe>
        </div>

        <div class="mt-2 flex items-center gap-3">
          <Button
            variant="outline"
            class="flex-1"
            onClick={() => setIsAvailabilityModalOpen(false)}
          >
            {t("availability.cancel")}
          </Button>
          <Button
            class="flex-1"
            disabled={updateProfileAvailabilityMutation.isPending}
            onClick={() => form.handleSubmit()}
          >
            {t("availability.save")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
